import { Subject, Subscription } from "rxjs"
import DOM from "../core/dom"
import Player, { PlayerDestroyData } from "../player"

/**
 * Represents a model for the data to be used in the
 * `Playback.update` RxJS subject.
 * 
 * @author Stan Hurks
 */
export interface PlaybackUpdateData {
    /**
     * The player id to update
     */
    playerId?: number

    /**
     * The data that has been changed
     */
    changedData: Partial<Playback>
}

/**
 * Represents playback information for a `Player`.
 * 
 * @author Stan Hurks
 */
export default class Playback {
    /**
     * Executes when the volume of the associated player has been changed or (un)muted.
     */
    public static volumeChanged: Subject<number> = new Subject()

    /**
     * Update the playback data.
     */
    public static update: Subject<PlaybackUpdateData> = new Subject()

    /**
     * Whether a video source has been loaded
     */
    public sourceLoaded: boolean = false
    
    /**
     * The current time of the video in ms.
     * 
     * Default: 0
     */
    public currentTimeMs: number = 0

    /**
     * The ID of the player
     */
    private readonly playerId!: number

    /**
     * The subscription to the `Playback.update` RxJS subject.
     */
    private readonly subscriptionUpdate!: Subscription

    /**
     * The subscription to the `Player.destroy` RxJS subject
     */
    private readonly subscriptionDestroy!: Subscription

    /**
     * The data used in the `this.initialize` function.
     */
    private initializeData: {
        /**
         * The observer ID for the `MutationObserver` that observes the root element
         */
        rootObserverId: number | null

        /**
         * The subscription to DOM changes by the mutation observer
         */
        subscriptionDOMChanged: Subscription | null

        /**
         * When observing is not supported, this interval will be used
         * instead as a fallback.
         */
        intervalFallback: NodeJS.Timeout | null
    } = {
        rootObserverId: null,
        subscriptionDOMChanged: null,
        intervalFallback: null
    }

    constructor(playerId: number) {
        this.playerId = playerId
        this.subscriptionUpdate = Playback.update.subscribe(this.onUpdate)
        this.subscriptionDestroy = Player.destroy.subscribe(this.onDestroy)
        
        this.initialize()
    }

    /**
     * Initialize the playback functionality
     */
    private initialize = () => {
        console.debug(`Playback functionality initializing... (#${this.playerId})`)

        // Find the root element for the player
        const rootElement = document.querySelector(`.${Player.classPrefix}${this.playerId}`)
        if (!rootElement) {
            console.error(`Could not initialize playback, the root container for the player is missing. Destroying playback... (#${this.playerId})`)

            this.onDestroy(this.playerId)
            return
        }

        // Observe until the video element is found
        this.destroyInitializeData()
        this.initializeData.rootObserverId = DOM.observe(rootElement)
        
        // Observing is supported
        if (this.initializeData.rootObserverId !== null) {
            this.initializeData.subscriptionDOMChanged = DOM.changed.subscribe((observerId) => {
    
                // Check if the observer ID matches
                if (observerId !== this.initializeData.rootObserverId) {
                    return
                }
    
                // Find the video element
                this.executeInitialize()
                    .then((finished) => {
                        
                        // Destroy the initialization data when its finished
                        if (finished) {
                            this.destroyInitializeData()
                        }
                    })
                    .catch((err) => {
                        console.error(err)
                    })
            })
            DOM.changed.next(this.initializeData.rootObserverId)
        }

        // If not supported, fall back to a 300ms interval
        else {
            this.initializeData.intervalFallback = setInterval(() => {
                this.executeInitialize()
                    .then((finished) => {

                        // Destroy the initialization data when its finished
                        if (finished) {
                            this.destroyInitializeData()
                        }
                    })
                    .catch((err) => {
                        console.error(err)
                    })
            }, 300)
        }
    }

    /**
     * The function to start observing for a video element for the `this.initialize` function.
     * 
     * When the video element is found, the function will attach event listeners to the video element,
     * as well as potential other initialization functionality that needs to be executed after that point.
     * 
     * When this process is done, the `Promise` will resolve with `true`, otherwise it will resolve as `false`.
     * 
     * When a fatal error occurs that causes to destroy this utility instance, the `Promise` will reject.
     */
    private executeInitialize = (): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            console.debug(`Observing root container for video element... (#${this.playerId})`)
        
            // Find the root element for the player
            const rootElement = document.querySelector(`.${Player.classPrefix}${this.playerId}`)
            if (!rootElement) {
                this.onDestroy(this.playerId)

                return reject(new Error(`Playback destroyed: The root container for the player is missing. (#${this.playerId})`))
            }

            // Find the video element
            const videoElement = document.querySelector(`.${Player.classPrefix}${this.playerId} video`)
            if (videoElement) {
                console.debug(`Video element found. (#${this.playerId})`)
                
                // Add event listeners
                videoElement.addEventListener('timeupdate', this.onTimeupdate)

                // Process is done
                resolve(true)
            }
            else {
                console.debug(`Video not found, observing for mutations in root container... (#${this.playerId})`)
                
                // Process needs to continue after DOM changes
                resolve(false)
            }
        })
    }

    /**
     * Executes when the `Playback.update` RxJS subject has been called.
     * 
     * @param data The data passed by RxJS
     */
    private onUpdate = (data: PlaybackUpdateData) => {
        
        // Loop through all changed properties
        for (const property of Object.keys(data.changedData)) {
            const value = (data.changedData as any)[property]
            
            // Change the value on `this`.
            if (property in this) {
                (this as any)[property] = value
            }
        }
    }

    /**
     * Executes when the `Player.destroy` RxJS subject has been called.
     * 
     * @param data The data passed by RxJS
     */
    private onDestroy = (data: PlayerDestroyData) => {
        // Only destroy the player when the `all` option is selected,
        // or when the id in data matches that of the current player.
        if (data !== 'all' && data !== this.playerId) {
            return
        }

        // Destroy subscriptions
        this.subscriptionDestroy.unsubscribe()
        this.subscriptionUpdate.unsubscribe()

        // Destroy initialization data
        this.destroyInitializeData()

        // Remove event listeners
        const videoElement = document.querySelector(`.${Player.classPrefix}${this.playerId} video`)
        if (videoElement) {
            videoElement.removeEventListener('timeupdate', this.onTimeupdate)
        }

        console.debug(`Playback destroyed. (#${this.playerId})`)
    }

    /**
     * The callback for the `timeupdate` event on the video element
     * within the player's container.
     * 
     * @param e The event
     */
    private onTimeupdate = (e: Event) => {

        // Type the event
        const event: Event & {
            time: number
        } = e as any

        // No time property on event AND no currentTime property on target element
        if (event.time !== 0 && !event.time && !(event.target as any).currentTime) {
            
            console.error(`No time property on timeupdate event and no currentTime property on event.target. (#${this.playerId})`)
            return
        }

        // The current playtime in ms
        this.currentTimeMs = Math.floor((event.time || (event.target as any).currentTime) * 1000)

        // Update the playback
        Playback.update.next({
            playerId: this.playerId,
            changedData: {
                currentTimeMs: this.currentTimeMs
            }
        })
    }

    /**
     * Destroys the data used for `this.initialize`.
     */
    private destroyInitializeData = () => {
        console.debug(`Destroying playback initialization data... (#${this.playerId})`)

        // Destroy subscriptions and observers
        this.initializeData.subscriptionDOMChanged?.unsubscribe()
        if (this.initializeData.rootObserverId !== null) {
            DOM.unobserve(this.initializeData.rootObserverId)
        }

        // Clear interval
        if (this.initializeData.intervalFallback !== null) {
            clearInterval(this.initializeData.intervalFallback)
        }

        // Reset data
        this.initializeData = {
            ...this.initializeData,
            rootObserverId: null,
            subscriptionDOMChanged: null,
            intervalFallback: null
        }
    }
}